import { PostgrestResponse } from "@supabase/supabase-js";
import { IPessoas } from "../../../types/tables/IPessoas";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Serviço para gerenciamento de pessoas com isolamento multi-tenant
 * Gerencia alunos, responsáveis, operadores e administradores
 */
export class PessoasService extends BaseMultiTenantService<IPessoas> {
  constructor() {
    super(
      "pessoas",
      ["nome", "email", "documento_numero", "cod_integracao"],
      "fk_id_organizacao",
    );
  }

  /**
   * Busca pessoas por tipo específico (Aluno, Responsável, etc.)
   */
  async getByTipo(
    organizationId: number,
    tipoId: number,
  ): Promise<PostgrestResponse<IPessoas[]>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_tipo_pessoa", tipoId)
      .is("deleted_at", null);
  }

  /**
   * Busca pessoa por documento
   */
  async getByDocumento(
    organizationId: number,
    documento: string,
  ): Promise<PostgrestResponse<IPessoas>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("documento_numero", documento)
      .is("deleted_at", null)
      .single();
  }

  /**
   * Busca pessoa por matrícula
   */
  async getByMatricula(
    organizationId: number,
    matricula: string,
  ): Promise<PostgrestResponse<IPessoas>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    tipo_pessoa:fk_id_tipo_pessoa(
                         id,
                         nome,
                         descricao
                    )
               `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("matricula", matricula)
      .is("deleted_at", null)
      .single();
  }

  /**
   * Busca pessoa por email
   */
  async getByEmail(
    organizationId: number,
    email: string,
  ): Promise<PostgrestResponse<IPessoas>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    tipo_pessoa:fk_id_tipo_pessoa(
                         id,
                         nome,
                         descricao
                    )
               `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("email", email)
      .is("deleted_at", null)
      .single();
  }

  /**
   * Busca pessoa por auth_user_id (ID do Supabase Auth)
   */
  async getByAuthUserId(
    authUserId: string,
  ): Promise<PostgrestResponse<IPessoas>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq("auth_user_id", authUserId)
      .is("deleted_at", null)
      .single();
  }

  /**
   * Busca alunos de um responsável específico - consulta simples na tabela
   */
  async getAlunosByResponsavel(
    organizationId: number,
    responsavelId: number,
  ): Promise<PostgrestResponse<IPessoas[]>> {
    return await this.client
      .from("pessoas_dependentes")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_responsavel", responsavelId);
  }

  /**
   * Busca responsáveis de um aluno específico - consulta simples na tabela
   */
  async getResponsaveisByAluno(
    organizationId: number,
    alunoId: number,
  ): Promise<PostgrestResponse<IPessoas[]>> {
    return await this.client
      .from("pessoas_dependentes")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_aluno", alunoId);
  }

  /**
   * Verifica se a pessoa pode ser excluída - consultas simples nas tabelas
   */
  async canDelete(
    id: number,
    organizationId: number,
  ): Promise<{ canDelete: boolean; reason?: string }> {
    // Verificar se é responsável com dependentes
    const { data: dependentes } = await this.client
      .from("pessoas_dependentes")
      .select("id")
      .eq("fk_id_responsavel", id)
      .eq("fk_id_organizacao", organizationId);

    if (dependentes && dependentes.length > 0) {
      return {
        canDelete: false,
        reason: "Pessoa possui dependentes vinculados",
      };
    }

    // Verificar se possui vendas
    const { data: vendas } = await this.client
      .from("vendas")
      .select("id")
      .or(
        `fk_id_aluno.eq.${id},fk_id_responsavel.eq.${id},fk_id_operador.eq.${id}`,
      )
      .eq("fk_id_organizacao", organizationId)
      .limit(1);

    if (vendas && vendas.length > 0) {
      return { canDelete: false, reason: "Pessoa possui histórico de vendas" };
    }

    return { canDelete: true };
  }
}

export const pessoasService = new PessoasService();
